import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { cn } from '@/lib/utils';

export interface SignaturePadHandle {
    clear: () => void;
    isEmpty: () => boolean;
    /** The drawing as a transparent PNG, or null if nothing has been drawn. */
    toPngBlob: () => Promise<Blob | null>;
}

interface SignaturePadCanvasProps {
    /** Fires whenever the pad goes from empty to drawn or back. */
    onInkChange?: (hasInk: boolean) => void;
    className?: string;
}

/**
 * A signature pad that survives device pixel ratios and container resizes.
 *
 * THE CANVAS IS RESIZED BY REDRAWING, NOT BY SCALING. A canvas whose backing store is
 * resized loses its bitmap, so the strokes are re-applied from `toData()` afterwards —
 * without that, rotating a tablet erases a signature somebody has already drawn.
 *
 * The ink is drawn BLACK on TRANSPARENT, because it is composited onto a white PDF page
 * later. A pad that drew in the theme's foreground colour would put white ink on white
 * paper the first time an admin worked in dark mode.
 */
const SignaturePadCanvas = forwardRef<SignaturePadHandle, SignaturePadCanvasProps>(
    ({ onInkChange, className }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement | null>(null);
        const padRef = useRef<SignaturePad | null>(null);
        const [hasInk, setHasInk] = useState(false);

        useEffect(() => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const pad = new SignaturePad(canvas, {
                penColor: '#111111',
                backgroundColor: 'rgba(0,0,0,0)',
                minWidth: 0.9,
                maxWidth: 2.6,
            });
            padRef.current = pad;

            const resize = () => {
                const ratio = Math.max(window.devicePixelRatio || 1, 1);
                const data = pad.toData();
                canvas.width = canvas.offsetWidth * ratio;
                canvas.height = canvas.offsetHeight * ratio;
                canvas.getContext('2d')?.scale(ratio, ratio);
                pad.clear();
                if (data.length > 0) pad.fromData(data);
            };

            resize();
            window.addEventListener('resize', resize);

            const handleEnd = () => {
                const ink = !pad.isEmpty();
                setHasInk(ink);
                onInkChange?.(ink);
            };
            pad.addEventListener('endStroke', handleEnd);

            return () => {
                window.removeEventListener('resize', resize);
                pad.removeEventListener('endStroke', handleEnd);
                pad.off();
                padRef.current = null;
            };
            // The pad is created once per mount; `onInkChange` is read through the closure
            // above and re-subscribing on every render would drop strokes mid-signature.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useImperativeHandle(ref, () => ({
            clear: () => {
                padRef.current?.clear();
                setHasInk(false);
                onInkChange?.(false);
            },
            isEmpty: () => padRef.current?.isEmpty() ?? true,
            toPngBlob: async () => {
                const pad = padRef.current;
                const canvas = canvasRef.current;
                if (!pad || !canvas || pad.isEmpty()) return null;
                return await new Promise<Blob | null>((resolve) => {
                    canvas.toBlob((blob) => resolve(blob), 'image/png');
                });
            },
        }));

        return (
            <div
                className={cn(
                    'relative rounded-lg border-2 border-dashed bg-background',
                    hasInk ? 'border-border' : 'border-muted-foreground/30',
                    className,
                )}
            >
                <canvas
                    ref={canvasRef}
                    className="h-40 w-full touch-none rounded-lg"
                    aria-label="Signature pad"
                />
                {!hasInk && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                        Draw your signature here
                    </span>
                )}
            </div>
        );
    },
);

SignaturePadCanvas.displayName = 'SignaturePadCanvas';

export default SignaturePadCanvas;
